import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import Client from '../Client';
import Config from '../ClientConfig';

const filterFieldSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiInputBase-input': {
    fontSize: { xs: '16px', sm: '14px' },
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '13px', sm: '13px' },
  },
};

const filterBarSx = {
  px: { xs: 1, sm: 1.5 },
  py: { xs: 0.75, sm: 1 },
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', sm: '160px 220px auto' },
  gap: { xs: 1, sm: 1.25 },
  alignItems: 'end',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
};

const headerCellSx = (align, width) => ({
  padding: { xs: '8px 7px', sm: '7px' },
  textAlign: align,
  whiteSpace: 'nowrap',
  width,
  fontSize: { xs: '13px', sm: '13px', md: '14px' },
  backgroundColor: '#f6f4ef',
});

const bodyCellSx = (align, width) => ({
  padding: { xs: '8px 7px', sm: '7px' },
  textAlign: align,
  whiteSpace: align === 'left' ? 'normal' : 'nowrap',
  width,
  fontSize: { xs: '13.5px', sm: '13px', md: '14px' },
  lineHeight: 1.35,
});

const unitLabel = (value) => ({ 0: 'pc', 1: 'kg', 3: 'other', 9: 'undefined' }[Number(value)] || '');

const formatQuantity = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const requestMessage = (error, fallback) => {
  const body = error?.responseJSON;
  const direct = body?.detail || body?.error || body?.message;
  if (Array.isArray(direct)) return direct.join(' ');
  if (typeof direct === 'string') return direct;
  return fallback;
};

class CurrentStockUI extends React.Component {
  constructor(props) {
    super(props);
    this.client = props.client || new Client('none');
    this.state = {
      locations: [],
      productMap: {},
      date: dayjs().format('YYYY-MM-DD'),
      location: '',
      rows: [],
      loadingLookups: true,
      loading: false,
      errorText: '',
      infoText: '',
    };
    this._mounted = false;
    this._sequence = 0;
    this._requests = [];
  }

  componentDidMount() {
    this._mounted = true;
    this.loadLookups();
  }

  componentWillUnmount() {
    this._mounted = false;
    this._sequence += 1;
    this._requests.forEach(request => {
      if (request && typeof request.abort === 'function') request.abort();
    });
  }

  loadLookups = () => {
    const sequence = ++this._sequence;
    const locationsRequest = this.client.getDataAsync(
      Config.API_URL_PURCHASE_ORDER_RECOMMENDATION_FILTERS,
      {},
    );
    const productsRequest = this.client.getDataAsync(
      `${Config.API_URL_PRODUCT}list_products/`,
      { status: 2, isPurchased: 1, orderBy_name: 1 },
    );
    this._requests = [locationsRequest, productsRequest];

    Promise.all([Promise.resolve(locationsRequest), Promise.resolve(productsRequest)])
      .then(([lookupResponse, productResponse]) => {
        if (!this._mounted || sequence !== this._sequence) return;
        const locations = Array.isArray(lookupResponse?.locations) ? lookupResponse.locations : [];
        const sourceProducts = productResponse?.products || [];
        const products = Array.isArray(sourceProducts) ? sourceProducts : Object.values(sourceProducts);
        const productMap = {};
        products.forEach(product => {
          const id = product?.iref ?? product?.id;
          if (id === null || id === undefined) return;
          productMap[String(id)] = {
            name: product.name || product.description || String(id),
            isPurchased: Number(product.isPurchased ?? 1),
          };
        });
        this.setState({
          locations,
          productMap,
          loadingLookups: false,
          errorText: locations.length ? '' : 'No stock locations are available.',
        });
      })
      .catch(error => {
        if (!this._mounted || sequence !== this._sequence || error?.statusText === 'abort') return;
        this.setState({
          loadingLookups: false,
          errorText: requestMessage(error, 'Could not load stock filters.'),
        });
      });
  };

  fetchStock = () => {
    if (!this.state.location) {
      this.setState({ errorText: 'Select a stock location.', rows: [], infoText: '' });
      return;
    }
    const sequence = ++this._sequence;
    this.setState({ loading: true, errorText: '', infoText: '' });
    const request = this.client.getDataAsync(
      `${Config.API_URL_REPORTS_TRANSACTIONS}current_stock/`,
      { location: this.state.location, asof: this.state.date },
    );
    this._requests = [request];

    Promise.resolve(request).then(response => {
      if (!this._mounted || sequence !== this._sequence) return;
      const items = Array.isArray(response?.items) ? response.items : [];
      const rows = items.map((item, index) => {
        const productId = String(item?.productId ?? item?.product ?? item?.id ?? index);
        const product = this.state.productMap[productId] || {};
        return {
          productId,
          productName: item?.productName || product.name || productId,
          unit: unitLabel(item?.unit ?? item?.unitType),
          quantity: Number(item?.qty ?? item?.quantity ?? item?.stockQty),
          isPurchased: Number(item?.isPurchased ?? product.isPurchased ?? 1),
        };
      }).filter(row => (
        row.isPurchased === 1
        && Number.isFinite(row.quantity)
        && Math.abs(row.quantity) > 1e-9
      ));
      this.setState({
        rows,
        loading: false,
        infoText: rows.length ? '' : 'No non-zero purchased stock lines found.',
      });
    }).catch(error => {
      if (!this._mounted || sequence !== this._sequence || error?.statusText === 'abort') return;
      this.setState({
        rows: [],
        loading: false,
        errorText: requestMessage(error, 'Could not load current stock.'),
      });
    });
  };

  exportCsv = () => {
    if (!this.state.rows.length) return;
    const escape = value => {
      const text = String(value ?? '');
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = [
      ['Current Stock', ''],
      ['Date', this.state.date],
      ['Location', this.state.location],
      ['', ''],
      ['Product', 'Unit', 'Quantity'],
      ...this.state.rows.map(row => [row.productName, row.unit, row.quantity]),
    ].map(row => row.map(escape).join(','));
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `current_stock_${this.state.date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  render() {
    const disabled = this.state.loading || this.state.loadingLookups;
    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={filterBarSx}>
          <TextField
            variant="standard"
            size="small"
            type="date"
            label="Date"
            value={this.state.date}
            onChange={event => this.setState({ date: event.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={filterFieldSx}
          />
          <TextField
            select
            required
            variant="standard"
            size="small"
            label="Location"
            value={this.state.location}
            onChange={event => this.setState({ location: event.target.value })}
            disabled={this.state.loadingLookups}
            sx={filterFieldSx}
          >
            <MenuItem value="">Select location</MenuItem>
            {this.state.locations.map(location => {
              const id = String(location?.iref ?? location?.id ?? '');
              return <MenuItem key={id} value={id}>{location?.name || id}</MenuItem>;
            })}
          </TextField>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' }, alignItems: 'center', gap: 0.75, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
            <Button size="small" variant="contained" onClick={this.fetchStock} disabled={disabled}>Apply</Button>
            <Button size="small" variant="outlined" onClick={this.exportCsv} disabled={!this.state.rows.length}>CSV</Button>
          </Box>
        </Box>

        {!!this.state.errorText && <Typography variant="caption" color="error" sx={{ px: 1, py: 0.5 }}>{this.state.errorText}</Typography>}
        {!!this.state.infoText && <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>{this.state.infoText}</Typography>}

        <TableContainer sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx('left', '65%')}><b>Product</b></TableCell>
                <TableCell sx={headerCellSx('center', '12%')}><b>Unit</b></TableCell>
                <TableCell sx={headerCellSx('right', '23%')}><b>Qty</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {this.state.rows.map(row => (
                <TableRow key={row.productId} hover>
                  <TableCell sx={bodyCellSx('left', '65%')}>{row.productName}</TableCell>
                  <TableCell sx={bodyCellSx('center', '12%')}>{row.unit}</TableCell>
                  <TableCell sx={bodyCellSx('right', '23%')}>{formatQuantity(row.quantity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {disabled && (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </TableContainer>
      </Box>
    );
  }
}

export default CurrentStockUI;
